/**
 * BFF Payment Types
 *
 * Types for payment-related BFF endpoints.
 * These types are shared between client (apps/client) and BFF (apps/api).
 */

// =============================================================================
// Deal Record Types
// =============================================================================

/**
 * Types of payment account deal records
 */
export enum DealRecordType {
  DEPOSIT = 'DEPOSIT', // 儲值
  REWARD = 'REWARD', // 儲值回饋
  CHARGE = 'CHARGE', // 訂單扣款
  REFUND = 'REFUND', // 退貨退款
  REVERT = 'REVERT', // 撤回訂單扣款
  ADJUST = 'ADJUST', // 餘額調整
  LOAN_PAYOUT = 'LOAN_PAYOUT', // 借款增加
  LOAN_REPAYMENT = 'LOAN_REPAYMENT', // 借款減少
  // -- 貨到付款 --
  COD_REQUEST = 'COD_REQUEST', // 發起貨到付款
  COD_PAY = 'COD_PAY', // 支付貨到付款
  COD_REFUND = 'COD_REFUND', // 退款貨到付款
  COD_ADJUST = 'COD_ADJUST', // 調整貨到付款相關帳戶餘額
  COD_REVERT = 'COD_REVERT', // 撤回 COD_REQUEST
}

/**
 * Deal record details — 不同 type 的 details 結構不同，
 * 這裡使用 optional fields 涵蓋所有類型。
 */
export interface DealRecordDetails {
  // CHARGE, REFUND, LOAN_PAYOUT: 訂單相關
  orderId?: string;
  orderNumber?: string;
  // ADJUST, COD_ADJUST: 說明文字
  description?: string;
  // ADJUST: 餘額調整請求
  balanceAdjustmentRequestId?: string;
  // DEPOSIT, REWARD: 儲值請求
  depositRequestId?: string;
  depositRequestPartitionId?: string;
  // REVERT: 被撤回的 deal record
  dealRecordId?: string;
  // LOAN_PAYOUT: 借款相關
  loanHistoryId?: string;
  paymentId?: string;
  loanRequestId?: string;
}

/**
 * Payment account deal record from Payment Core API
 */
export interface DealRecord {
  id: string;
  accountId?: string;
  type: DealRecordType | string;
  amount: number;
  balance?: number;
  dealtAt: string;
  createdAt: string;
  updatedAt: string;
  revertedAt?: string;
  metadata?: Record<string, unknown>;
  details?: DealRecordDetails;
  // Balance fields
  cashBalance: number;
  cashDifference?: number | null;
  rewardBalance: number;
  rewardDifference?: number | null;
  refundBalance: number;
  refundDifference?: number | null;
  codRefundBalance: number;
  codRefundDifference?: number | null;
  codOutstandingBalance?: number;
  codOutstandingDifference?: number | null;
  loanOutstandingBalance: number;
  loanOutstandingDifference?: number | null;
}

// =============================================================================
// Payment Types
// =============================================================================

/**
 * Types of payment methods
 */
export enum PaymentType {
  DEPOSIT = 'DEPOSIT',
  COD = 'COD',
  DEPOSIT_WITH_LOAN = 'DEPOSIT_WITH_LOAN',
}

/**
 * Payment status
 */
export enum PaymentStatus {
  PAID = 'paid',
  UNPAID = 'unpaid',
}

/**
 * Payment provider IDs used in Medusa
 */
export enum PaymentProviderId {
  COD = 'pp_marx_cod_marx',
  DEPOSIT = 'pp_marx_deposit_marx',
  DEPOSIT_WITH_LOAN = 'pp_marx_deposit-with-loan_marx',
}

/**
 * Loan request information embedded in payment
 */
export interface LoanRequestInfo {
  id: string;
  requestedAmount: number;
  outstandingBalance: number | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  settledAt: string | null;
}

/**
 * Payment record from Payment Core API
 */
export interface Payment {
  id: string;
  type: PaymentType | string;
  account: {
    id: string;
  };
  amount: number;
  pendingAmount: number;
  chargedAmount?: number;
  refundedAmount?: number;
  dealRecords?: DealRecord[];
  dealtAt: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
  loanRequest?: LoanRequestInfo | null;
}

// =============================================================================
// Settlement Request Types
// =============================================================================

export interface SettlementRequestOperator {
  id: string;
  name: string;
  uri: string;
}

export interface SettlementRequestBankInfo {
  account: string;
  date: string;
  code?: string;
  accountName?: string;
}

export interface SettlementRequestReference {
  type: 'PERIOD' | 'ORDER' | 'CUSTOM';
  period?: { year: number; month: number };
  orderId?: string;
  orderNumber?: string;
  description?: string;
}

/**
 * Settlement request from Payment Core API.
 *
 * Design Decision: approvedAt/rejectedAt Mutual Exclusivity
 * These fields are mutually exclusive at runtime (a request cannot be both
 * approved and rejected), but we use nullable fields instead of a discriminated
 * union because:
 * 1. Matches Payment Core API response structure exactly
 * 2. Simpler transformation logic in BFF
 * 3. Frontend status is derived: pending = both null, approved = approvedAt set
 *
 * Invariant (enforced by Payment Core): At most one of approvedAt/rejectedAt is non-null
 */
export interface SettlementRequest {
  id: string;
  amount: number;
  createdByType: 'STAFF' | 'CUSTOMER';
  customerRemark: string;
  staffRemark: string;
  bankInfo: {
    account: string;
    date: string;
    code?: string;
    accountName?: string;
  } | null;
  reference: SettlementRequestReference | null;
  loanRequest?: {
    id: string;
  } | null;
  /** ISO timestamp when approved, null if pending or rejected */
  approvedAt: string | null;
  /** ISO timestamp when rejected, null if pending or approved */
  rejectedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Monthly Settlement Types
// =============================================================================

/**
 * Billing period information (always based on the previous month)
 */
export interface BillingPeriod {
  /** Year of the billing period */
  year: number;
  /** Month of the billing period (1-12) */
  month: number;
  /** End date of the billing period (yyyy-MM-dd format) */
  end_date: string;
}

/**
 * Snapshot of account balances at the end of the billing period
 */
export interface LastMonthSnapshot {
  /** Total loan outstanding balance */
  loan_outstanding: number;
  /** Cash balance */
  cash_balance: number;
  /** Amount due (loan_outstanding - cash_balance) */
  amount_due: number;
  /** Reward balance */
  reward_balance: number;
  /** Refund balance */
  refund_balance: number;
  /** Snapshot date (ISO date string) */
  snapshot_date: string;
}

/**
 * Current month's repayment and loan progress
 */
export interface CurrentMonthProgress {
  /** Total repayment amount this month (LOAN_REPAYMENT) */
  total_repayment: number;
  /** Number of repayment transactions */
  repayment_count: number;
  /** Total new loan amount this month (LOAN_PAYOUT) */
  total_new_loan: number;
  /** Number of new loan transactions */
  new_loan_count: number;
}

/**
 * Current account status
 */
export interface CurrentStatus {
  /** Current loan outstanding balance */
  loan_outstanding: number;
  /** Current cash balance */
  cash_balance: number;
  /** Current reward balance */
  reward_balance: number;
  /** Current refund balance */
  refund_balance: number;
  /** Current amount due (loan_outstanding - cash_balance) */
  amount_due: number;
  /** Remaining amount from last month's billing that hasn't been repaid */
  remaining_from_last_month: number;
}

/**
 * Pending settlement requests information
 */
export interface PendingRequestsInfo {
  /** Total amount of pending requests */
  total: number;
  /** Number of pending requests */
  count: number;
  /** Whether there's a pending PERIOD request for the current billing month */
  has_current_month_period: boolean;
  /** List of pending settlement requests */
  requests: SettlementRequest[];
}

/**
 * Monthly settlement summary combining all settlement-related data
 */
export interface MonthlySettlementSummary {
  /** Billing period information */
  billing_period: BillingPeriod;
  /** Snapshot at the end of billing period */
  last_month_snapshot: LastMonthSnapshot;
  /** Current month's repayment progress */
  current_month_progress: CurrentMonthProgress;
  /** Current account status */
  current: CurrentStatus;
  /** Pending settlement requests */
  pending_requests: PendingRequestsInfo;
}

/**
 * Response wrapper for monthly settlement endpoint
 */
export interface MonthlySettlementResponse {
  monthly_settlement: MonthlySettlementSummary;
}
